import { forwardRef } from "react";
import { cn } from "@/lib/utils";

// Row Component
export interface RowProps extends React.HTMLAttributes<HTMLDivElement> {
	gutter?: number | [number, number];
	align?: "top" | "middle" | "bottom" | "stretch";
	justify?: "start" | "end" | "center" | "space-between" | "space-around" | "space-evenly";
	wrap?: boolean;
}

const Row = forwardRef<HTMLDivElement, RowProps>(
	(
		{
			className,
			gutter = 0,
			align = "top",
			justify = "start",
			wrap: _wrap = true,
			style,
			children,
			...props
		},
		ref,
	) => {
		const alignMap = {
			top: "items-start",
			middle: "items-center",
			bottom: "items-end",
			stretch: "items-stretch",
		};

		const justifyMap = {
			start: "justify-start",
			end: "justify-end",
			center: "justify-center",
			"space-between": "justify-between",
			"space-around": "justify-around",
			"space-evenly": "justify-evenly",
		};

		const [horizontalGutter, verticalGutter] = Array.isArray(gutter) ? gutter : [gutter, gutter];

		// 使用 CSS Grid 来实现响应式布局，更简洁
		const rowStyle: React.CSSProperties = {
			display: "grid",
			gridTemplateColumns: "repeat(24, minmax(0, 1fr))",
			columnGap: horizontalGutter || undefined,
			rowGap: verticalGutter || undefined,
			...style,
		};

		return (
			<div
				ref={ref}
				className={cn(alignMap[align], justifyMap[justify], className)}
				style={rowStyle}
				{...props}
			>
				{children}
			</div>
		);
	},
);

Row.displayName = "Row";

// Col Component
interface ColSize {
	span?: number;
	offset?: number;
}

export interface ColProps extends React.HTMLAttributes<HTMLDivElement> {
	span?: number;
	offset?: number;
	xs?: number | ColSize;
	sm?: number | ColSize;
	md?: number | ColSize;
	lg?: number | ColSize;
	xl?: number | ColSize;
	xxl?: number | ColSize;
	flex?: string | number;
}

const Col = forwardRef<HTMLDivElement, ColProps>(
	(
		{
			className,
			span = 24,
			offset = 0,
			xs,
			sm,
			md,
			lg,
			xl,
			xxl,
			flex: _flex,
			style,
			children,
			...props
		},
		ref,
	) => {
		// 解析响应式配置
		const parseSize = (size: number | ColSize | undefined) => {
			if (size === undefined) return undefined;
			if (typeof size === "number") return { span: size, offset: 0 };
			return size;
		};

		// 解析各断点配置
		const xsConfig = parseSize(xs);
		const smConfig = parseSize(sm);
		const mdConfig = parseSize(md);
		const lgConfig = parseSize(lg);
		const xlConfig = parseSize(xl);
		const xxlConfig = parseSize(xxl);

		// 使用 CSS Grid span，考虑响应式
		// 默认使用 xs 或 span
		const defaultSpan = xsConfig?.span ?? span;

		const colStyle: React.CSSProperties = {
			gridColumn: `span ${defaultSpan}`,
			marginLeft: offset > 0 ? `${(offset / 24) * 100}%` : undefined,
			...style,
		};

		// 生成响应式类名 - 使用 Tailwind 的响应式前缀
		const getColSpanClass = (s: number, prefix = "") => {
			const p = prefix ? `${prefix}:` : "";
			// 使用 col-span- 类或直接使用 style
			switch (s) {
				case 24:
					return `${p}col-span-full`;
				case 12:
					return `${p}col-span-12`;
				case 8:
					return `${p}col-span-8`;
				case 6:
					return `${p}col-span-6`;
				case 4:
					return `${p}col-span-4`;
				default:
					return "";
			}
		};

		const responsiveClasses: string[] = [];

		if (smConfig?.span !== undefined) {
			responsiveClasses.push(getColSpanClass(smConfig.span, "sm"));
		}
		if (mdConfig?.span !== undefined) {
			responsiveClasses.push(getColSpanClass(mdConfig.span, "md"));
		}
		if (lgConfig?.span !== undefined) {
			responsiveClasses.push(getColSpanClass(lgConfig.span, "lg"));
		}
		if (xlConfig?.span !== undefined) {
			responsiveClasses.push(getColSpanClass(xlConfig.span, "xl"));
		}
		if (xxlConfig?.span !== undefined) {
			responsiveClasses.push(getColSpanClass(xxlConfig.span, "2xl"));
		}

		return (
			<div
				ref={ref}
				className={cn("box-border", ...responsiveClasses.filter(Boolean), className)}
				style={colStyle}
				{...props}
			>
				{children}
			</div>
		);
	},
);

Col.displayName = "Col";

export { Row, Col };
